import { Row, Col, Drawer, Button, Menu, Skeleton } from "antd";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../../../store";
import { setCollapsed } from "../../../redux/slices/customerSlice";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import Sider from "antd/es/layout/Sider";
import { LogoDark } from "../Assets";
import SelectDropdownCustSiteMobile from "../SelectDropdownCustSiteMobile";
import { useSidebarLogic } from "../hooks/useSidebarLogic";

interface SidebarProps {
    isMobile: boolean;
    presentComponents: any;
    isNewIndustry: boolean
}

export default function Sidebar({ isMobile, presentComponents, isNewIndustry }: SidebarProps) {
    const dispatch = useDispatch();

    const {
        customerSites,
        customerSitesLoading,
        permittedCustomersLoading,
        selectedCustomerName,
        selectedSite,
        selectedKey,
        collapsed
    } = useSelector((state: RootState) => state.userInfo);

    const { menuItems, lastSegment, logo } = useSidebarLogic(presentComponents, isNewIndustry);

    const handleSiderClose = () => {
        dispatch(setCollapsed(!collapsed));
    };

    const closeDrawer = () => {
        if (isMobile && collapsed) {
            dispatch(setCollapsed(false));
        }
    };

    const handleMenuClick = () => {
        closeDrawer();
    };

    const siderContent = (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            {!isMobile && (
                <div className="imageDiv" style={{ flexShrink: 0 }}>
                    <img src={logo} alt="logo" className="actualImage" />
                </div>
            )}
            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <SelectDropdownCustSiteMobile
                            isMobile={isMobile}
                            lastSegment={lastSegment}
                            onClose={closeDrawer}
                            isNewIndustry={isNewIndustry}
                        />
                        <Skeleton
                            active={true}
                            title={false}
                            className="margin-5 newLoadingSkeleton"
                            loading={(customerSites.length === 0 && customerSitesLoading) || permittedCustomersLoading}
                            paragraph={{ rows: 5 }}
                        >
                            <Menu
                                theme={"dark"}
                                mode="inline"
                                defaultSelectedKeys={[selectedCustomerName, selectedSite]}
                                selectedKeys={[selectedCustomerName, selectedSite, selectedKey]}
                                items={menuItems}
                                onClick={handleMenuClick}
                            />
                        </Skeleton>
                    </Col>
                </Row>
            </div>
            <div style={{
                padding: '16px',
                textAlign: 'center',
                color: 'rgba(255, 255, 255, 0.45)',
                fontSize: '12px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                flexShrink: 0
            }}>
                v{__APP_VERSION__}
            </div>
        </div>
    );

    if (isMobile) {
        return (
            <Drawer
                className="clrDrawer"
                title={
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <Button
                                type="text"
                                icon={collapsed ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
                                onClick={handleSiderClose}
                                className="mr-10 text-base"
                            />
                            <div>Menu</div>
                        </div>
                        <div className="imageDiv">
                            <img src={LogoDark} alt="logo" className="actualImage" />
                        </div>
                    </div>
                }
                placement="left"
                closable={false}
                onClose={() => dispatch(setCollapsed(!collapsed))}
                open={collapsed}
            >
                {siderContent}
            </Drawer>
        );
    }

    return (
        <Sider trigger={null} collapsible collapsed={collapsed} theme="dark">
            {siderContent}
        </Sider>
    );
}
